// Imports
import { z } from 'zod';


// Initialize
const historyStateNamespace = 'sessionScopedStorage';


// Handles checking for session storage support
const checkIfSessionStorageAvailable = () => {
	try {
		const testKey = 'test-session-storage-support';
		sessionStorage.setItem(testKey, testKey);
		sessionStorage.removeItem(testKey);
		return true;
	} catch (e) {
		return false;
	}
};


// Determine the storage tier once, so reads and writes never disagree about where the data lives
const isSessionStorageAvailable = checkIfSessionStorageAvailable();


// Type the history state object
const historyStateSchema = z
	.object({
		[historyStateNamespace]: z.record(z.string()),
	})
	.deepPartial()
	.passthrough()
	.nullable();


// Retrieves a copy of our namespace within the browser's history state
const getHistoryStateValues = (): Record<string, string | undefined> => {
	const historyState = historyStateSchema.safeParse(window.history.state);
	
	if (!historyState.success) {
		return {};
	}
	
	return { ...historyState.data?.[historyStateNamespace] };
};


// Holds the fallback values, hydrated from the browser's history state
const fallbackValues = new Map(Object.entries(getHistoryStateValues()));


// Replicates the fallback values into the browser's history state, so they survive a page reload
const persistFallbackValues = () => {
	try {
		window.history.replaceState(
			{ ...window.history.state, [historyStateNamespace]: Object.fromEntries(fallbackValues) },
			''
		);
	} catch (e) {
		// Do nothing
	}
};


/** Like `sessionStorage`, but falls back to an in-memory store, replicated into the browser's history state, when session storage is unavailable. */
const sessionScopedStorage = {
	/** Retrieve an item */
	getItem(name: string): string | null {
		// Read from session storage when it's available
		if (isSessionStorageAvailable) {
			return sessionStorage.getItem(name);
		}
		
		
		// Otherwise, read from the fallback values
		const value = fallbackValues.get(name);
		
		
		// Return
		return value ?? null;
	},
	
	
	/** Store an item */
	setItem(name: string, value: string) {
		// Write to session storage when it's available
		if (isSessionStorageAvailable) {
			try {
				sessionStorage.setItem(name, value);
			} catch (e) {
				// If the write fails, remove whatever value it was replacing, so a stale one can't be read back as if the write had succeeded
				try {
					sessionStorage.removeItem(name);
				} catch (e) {
					// Do nothing
				}
			}
			
			return;
		}
		
		
		// Otherwise, store the value within the fallback values
		fallbackValues.set(name, value);
		persistFallbackValues();
	},
	
	
	/** Remove an item */
	removeItem(name: string) {
		// Remove from session storage when it's available
		if (isSessionStorageAvailable) {
			try {
				sessionStorage.removeItem(name);
			} catch (e) {
				// Do nothing
			}
			
			return;
		}
		
		
		// Otherwise, remove the value from the fallback values
		fallbackValues.delete(name);
		persistFallbackValues();
	},
};

export default sessionScopedStorage;
